import React, { useRef, useState } from "react";
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./modal_billing_for_wo.css";
import useSWR from 'swr';
import axios from 'axios';
import { Resizable } from 'react-resizable';
import type { ResizeCallbackData } from 'react-resizable';

interface DataType {
    key: string;
    customerCode: string;
    customerName: string;
    customerType: string;
    address1: string;
    address2: string;
    phoneNumber: string;
    provinsi: string;
    provinsiCode: string;
    kabupaten: string;
    kabupatenCode: string;
    kecamatan: string;
    kecamatanCode: string;
    kelurahan: string;
    kelurahanCode: string;
    kodePos: string;
}

interface ModalBillingForWoProps {
    onBillingSelect: (record: DataType) => void;
}

type DataIndex = keyof DataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);

const ResizableTitle = (props: React.HTMLAttributes<any> & { onResize: (e: React.SyntheticEvent, data: ResizeCallbackData) => void; width: number; onDoubleClick: () => void; }) => {
    const { onResize, width, onDoubleClick, ...restProps } = props;

    if (!width) {
        return <th {...restProps} onDoubleClick={onDoubleClick} />;
    }

    return (
        <Resizable
            width={width}
            height={0}
            handle={
                <span
                    className="react-resizable-handle"
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                />
            }
            onResize={onResize}
            draggableOpts={{ enableUserSelectHack: false }}
        >
            <th {...restProps} onDoubleClick={onDoubleClick} />
        </Resizable>
    );
};


const ModulBillingForWo: React.FC<ModalBillingForWoProps> = ({ onBillingSelect }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const { data, error } = useSWR<DataType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/work-order/billing-customers`, fetcher);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };


    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };


    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            String(record[dataIndex])
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });


    const [columns, setColumns] = useState<TableColumnsType<DataType>>([
        {
            title: 'Customer Code',
            dataIndex: 'customerCode',
            key: 'customerCode',
            width: 150,
            sorter: (a, b) => (a.customerCode || '').localeCompare(b.customerCode || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('customerCode'),
            ellipsis: true,
        },
        {
            title: 'Customer Name',
            dataIndex: 'customerName',
            key: 'customerName',
            width: 200,
            sorter: (a, b) => (a.customerName || '').localeCompare(b.customerName || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('customerName'),
            ellipsis: true,
        },
        {
            title: 'Customer Type',
            dataIndex: 'customerType',
            key: 'customerType',
            width: 150,
            sorter: (a, b) => (a.customerType || '').localeCompare(b.customerType || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('customerType'),
            ellipsis: true,
        },
        {
            title: 'Address 1',
            dataIndex: 'address1',
            key: 'address1',
            width: 250,
            sorter: (a, b) => (a.address1 || '').localeCompare(b.address1 || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('address1'),
            ellipsis: true,
        },
        {
            title: 'Address 2',
            dataIndex: 'address2',
            key: 'address2',
            width: 250,
            sorter: (a, b) => (a.address2 || '').localeCompare(b.address2 || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('address2'),
            ellipsis: true,
        },
        {
            title: 'Phone Number',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            width: 150,
            sorter: (a, b) => (a.phoneNumber || '').localeCompare(b.phoneNumber || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('phoneNumber'),
            ellipsis: true,
        },
    ]);


    const autoSizeColumn = (dataIndex: DataIndex) => {
        if (!data) return;

        const FONT_STYLE = "14px 'Poppins', sans-serif";
        const PADDING = 32;

        const getTextWidth = (text: string, font: string): number => {
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            if (context) {
                context.font = font;
                return context.measureText(text).width;
            }
            return 0;
        };

        const headerColumn = columns.find(col => col.key === dataIndex);
        const headerText = headerColumn && headerColumn.title ? String(headerColumn.title) : '';
        const headerWidth = getTextWidth(headerText, `600 ${FONT_STYLE}`) + PADDING;

        const cellWidths = data.map(item => {
            const cellText = item[dataIndex] ? String(item[dataIndex]) : '';
            return getTextWidth(cellText, FONT_STYLE) + PADDING;
        });

        const maxContentWidth = Math.ceil(Math.max(headerWidth, ...cellWidths, 50));

        setColumns(prevColumns => {
            const index = prevColumns.findIndex(col => col.key === dataIndex);
            if (index === -1) return prevColumns;
            let newWidth = maxContentWidth;
            const newColumns = [...prevColumns];
            newColumns[index] = {
                ...newColumns[index],
                width: newWidth,
            };
            return newColumns;
        });
    };

    const handleResize = (index: number) => (
        e: React.SyntheticEvent,
        { size }: ResizeCallbackData
    ) => {
        setColumns((prevColumns) => {
            const newColumns = [...prevColumns];
            newColumns[index] = {
                ...newColumns[index],
                width: size.width,
            };
            return newColumns;
        });
    };


    const resizableColumns = columns.map((col, index) => ({
        ...col,
        onHeaderCell: (column: any) => ({
            width: column.width,
            onResize: handleResize(index),
            onDoubleClick: () => autoSizeColumn(column.dataIndex as DataIndex),
        }),
    }));

    const components = {
        header: {
            cell: ResizableTitle,
        },
    };

    const tableWidth = columns.reduce((sum, col) => sum + (col.width as number || 0), 0);

    return (
        <div id="isyy" style={transformStyle}>
            <div className="gjs-grid-row" id="ia3lj">
                <div className="gjs-grid-column" id="izbqo">
                    <Table<DataType>
                        bordered
                        components={components}
                        columns={resizableColumns as any}
                        dataSource={data}
                        loading={!data && !error}
                        scroll={{ x: tableWidth }}
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                onBillingSelect(record);
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default ModulBillingForWo;